import toggleLoginDrawer from "@NA/Handlers/ADILoginDrawerHandler";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import ADIColors from "@NA/Utils/ADIColors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getPageLinkByNodeId,
    LinkModel,
    useGetLink,
    useGetLinks,
} from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import * as React from "react";
import { FC } from "react";
import { connect, ResolveThunks, useSelector } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    isAuthenticated: state.context.session.isAuthenticated,
});

const mapDispatchToProps = {
    toggleLoginDrawer,
};

const enum fields {
    direction = "direction",
    alignment = "alignment",
    title = "title",
    titleLink = "titleLink",
    links = "links",
}

export interface LinkModelCustom {
    fields: {
        openInNewWindow: boolean;
        overriddenTitle: string;
        destination: LinkFieldValue;
        loginRequired: boolean;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.direction]: "vertical" | "horizontal";
        [fields.alignment]: "left" | "center" | "right";
        [fields.title]: string;
        [fields.titleLink]: LinkFieldValue;
        [fields.links]: LinkModelCustom[];
    };
    extendedStyles?: LinkListStyles;
}

export interface LinkListStyles {
    linkListWrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    linkWrapper?: InjectableCss;
    leftMegaMenuLink?: LinkPresentationProps;
}

export const linkListStyles: LinkListStyles = {
    linkListWrapper: {
        css: css`
            text-transform: capitalize;
            padding: 0px 0;
        `,
    },
    titleWrapper: {
        css: css`
            font-weight: bold;
        `,
    },
    linkWrapper: {
        css: css`
            padding: 1px 18px 0 0;
        `,
    },
    leftMegaMenuLink: {
        css: css`
            > span {
                color: ${getColor("text.main")} !important;
                max-width: 100%;
                flex-basis: 100%;
                padding: 0px 15px 4px 10px;
                margin-left: 5px;
                font-size: 12px;
                width: 100%;
                max-height: 28px;
                display: inline-block;
                font-weight: bold !important;
            }
            &:hover {
                > span {
                    text-decoration: underline;
                }
            }
            &:focus {
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            null,
                            css`
                                outline-color: ${ADIColors.darkBlack3};
                                outline-style: ${getProp("theme.focus.style", "solid")};
                                outline-width: 0px;
                            `,
                        ],
                        "min",
                    );
                }}
            }
            &:focus-visible {
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            null,
                            css`
                                outline-color: ${ADIColors.darkBlack3};
                                outline-style: ${getProp("theme.focus.style", "solid")};
                                outline-width: 1px;
                            `,
                        ],
                        "min",
                    );
                }}
            }
        `,
    },
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export const MenuLink: FC<Props> = ({ fields, extendedStyles, isAuthenticated, toggleLoginDrawer }) => {
    const links = useGetLinks(fields.links, o => o.fields.destination);
    const isPendingVerification = useSelector(
        (state: CustomApplicationState) => state.ADIGlobalData.PricingStatusStore.isPendingVerification,
    );
    const titleLink = useGetLink(fields.titleLink);
    const styles = useMergeStyles("linkList", linkListStyles, extendedStyles);

    const alignmentStyles = css`
        text-align: ${fields.alignment || "right"};
        ${styles.linkListWrapper?.css}
    `;

    const directionStyles = css`
        ${fields.direction === "horizontal" ? "display: inline-block;" : "padding: 1px 0 0 0;"};
        ${styles.linkWrapper?.css}
    `;

    const openSignInDrawer = (returnUrl: string = "") => {
        toggleLoginDrawer({
            loginDrawer: true,
            returnUrl,
        });
    };

    const reloadPage = (getUrl: string) => {
        if (getUrl.includes("monthlyflyer")) {
            window.location.href = getUrl;
        }
    };

    return (
        <StyledWrapper css={alignmentStyles}>
            {titleLink.url ? (
                <StyledWrapper {...styles.titleWrapper}>
                    <Link
                        href={titleLink.url}
                        onClick={() => reloadPage(titleLink.url!)}
                        {...linkListStyles.leftMegaMenuLink}
                    >
                        {fields.title || titleLink.title}
                    </Link>
                </StyledWrapper>
            ) : titleLink.title ? (
                <StyledWrapper {...styles.titleWrapper}>
                    <Typography as="h3" {...styles.title}>
                        {fields.title}
                    </Typography>
                </StyledWrapper>
            ) : null}
            {links.map((link, index) => {
                if (fields.links[index].fields.loginRequired && !isAuthenticated) {
                    return (
                        <StyledWrapper key={index}>
                            {link.url && (
                                <Link
                                    {...linkListStyles.leftMegaMenuLink}
                                    className="child-link"
                                    onClick={() => openSignInDrawer(link.url)}
                                >
                                    {fields.links[index].fields.overriddenTitle || link.title}
                                </Link>
                            )}
                        </StyledWrapper>
                    );
                } else {
                    const linkField = fields.links[index]?.fields;
                    const title = linkField?.overriddenTitle || link.title;
                    if (isPendingVerification) {
                        if (title == "Monthly Sales Flyer") {
                            return null;
                        }
                    }
                    return (
                        <StyledWrapper key={index} css={directionStyles}>
                            {link.url && (
                                <Link
                                    href={link.url}
                                    onClick={() => reloadPage(link.url!)}
                                    {...linkListStyles.leftMegaMenuLink}
                                    className="child-link"
                                >
                                    {fields.links[index].fields.overriddenTitle || link.title}
                                </Link>
                            )}
                        </StyledWrapper>
                    );
                }
            })}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(MenuLink),
    definition: {
        group: "Basic",
        icon: "LinkList",
        displayName: "MenuList",
        fieldDefinitions: [
            {
                name: fields.direction,
                editorTemplate: "DropDownField",
                defaultValue: "vertical",
                fieldType: "General",
                options: [
                    {
                        displayName: "Vertical",
                        value: "vertical",
                    },
                    {
                        displayName: "Horizontal",
                        value: "horizontal",
                    },
                ],
                hideEmptyOption: true,
            },
            {
                name: fields.alignment,
                editorTemplate: "DropDownField",
                defaultValue: "left",
                fieldType: "General",
                options: [
                    {
                        displayName: "Left",
                        value: "left",
                    },
                    {
                        displayName: "Center",
                        value: "center",
                    },
                    {
                        displayName: "Right",
                        value: "right",
                    },
                ],
                hideEmptyOption: true,
            },
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                fieldType: "General",
                name: fields.titleLink,
                displayName: "Title Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const {
                        destination: { type, value },
                        overriddenTitle,
                    } = item.fields;
                    if (overriddenTitle) {
                        return overriddenTitle;
                    }
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        let displayValue = "";
                        const categories = (state as any).pageEditor.categories;
                        if (!categories) {
                            displayValue = "Loading...";
                        } else {
                            const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                            if (matchingCategories.length === 0) {
                                displayValue = "Unknown";
                            } else {
                                displayValue = matchingCategories[0].shortDescription;
                            }
                        }

                        return displayValue;
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                        allowUrls: item => {
                            return false;
                        },
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "loginRequired",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
